import { createContext, type FC, type ReactNode, useState } from "react";

export type DisplayTheme = "light" | "dark";

interface Settings {
  theme: DisplayTheme;
  openAccordions: boolean;
  expandListView: boolean;
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
  set?: <T extends keyof Settings>(key: T, value: Settings[T]) => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  theme: "light",
  openAccordions: false,
  expandListView: false,
};

const restoreSettings = (): Settings => {
  let settings = initialSettings;

  try {
    const storedData = window.localStorage.getItem("settings");

    if (storedData) {
      settings = JSON.parse(storedData) as Settings;
    } else {
      settings = {
        theme: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
        openAccordions: false,
        expandListView: false,
      };
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a stringified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem("settings", JSON.stringify(settings));
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => undefined,
});

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(restoreSettings());

  const saveSettings = (updatedSettings: Settings): void => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;
